/** 本地 UI 状态（Zustand，ADR §3：仅主题、布局）：侧栏 / Aside 折叠、密度、动效档位、StatusPill 状态。 */
import { create } from 'zustand'
import type { HeadingItem } from '../../shared/editor/headings.ts'
import type { EntryKind } from '../../shared/schemas/enums.ts'
import { type MotionLevel, setMotion as persistMotion, storedMotion } from './motion.ts'

export type PillStatus = 'idle' | 'synced' | 'connecting' | 'offline' | 'readOnly'

export type Density = 'comfortable' | 'compact'

interface LayoutState {
  density: Density
  setDensity: (d: Density) => void
  motion: MotionLevel
  setMotion: (m: MotionLevel) => void
  sidebarOpen: boolean
  asideOpen: boolean
  drawerOpen: boolean
  toggleSidebar: () => void
  toggleAside: () => void
  setDrawer: (v: boolean) => void
  setAside: (v: boolean) => void
}

const read = (k: string, d: boolean) => {
  try {
    const v = localStorage.getItem(k)
    return v === null ? d : v === '1'
  } catch {
    return d
  }
}
const write = (k: string, v: boolean) => {
  try {
    localStorage.setItem(k, v ? '1' : '0')
  } catch {
    /* ignore */
  }
}

const readDensity = (): Density => {
  try {
    return localStorage.getItem('xz:density') === 'compact' ? 'compact' : 'comfortable'
  } catch {
    return 'comfortable'
  }
}
/** 写 html[data-density]，tokens.css 据此切换 --xz-row-h。 */
export function applyDensity(d: Density): void {
  if (typeof document !== 'undefined') document.documentElement.dataset.density = d
}

export const useLayout = create<LayoutState>((set, get) => ({
  density: typeof window === 'undefined' ? 'comfortable' : readDensity(),
  setDensity: (density) => {
    try {
      localStorage.setItem('xz:density', density)
    } catch {
      /* ignore */
    }
    applyDensity(density)
    set({ density })
  },
  motion: typeof window === 'undefined' ? 'standard' : storedMotion(),
  setMotion: (motion) => {
    persistMotion(motion)
    set({ motion })
  },
  sidebarOpen: typeof window === 'undefined' ? true : read('xz:sidebar', true),
  asideOpen: typeof window === 'undefined' ? true : read('xz:aside', true),
  drawerOpen: false,
  toggleSidebar: () => {
    const v = !get().sidebarOpen
    write('xz:sidebar', v)
    set({ sidebarOpen: v })
  },
  toggleAside: () => {
    const v = !get().asideOpen
    write('xz:aside', v)
    set({ asideOpen: v })
  },
  setAside: (v) => set({ asideOpen: v }),
  setDrawer: (v) => set({ drawerOpen: v }),
}))

export const useStatus = create<{ status: PillStatus; set: (s: PillStatus) => void }>((set) => ({
  status: 'idle',
  set: (status) => set({ status }),
}))

/** Aside 内容由页面注入（04 §4：大纲 / 反链 / 评论 / 属性）；离开页面清空。 */
export const useAsideSlot = create<{
  node: import('react').ReactNode | null
  set: (n: import('react').ReactNode | null) => void
}>((set) => ({ node: null, set: (node) => set({ node }) }))

/** 新建空间 Dialog（侧栏 + 空间列表页两个入口共用，08 §2.5）。 */
/** 新建空间；`groupId` = 预选大类（列表页「在此新建」，ADR-0012）。 */
export const useCreateSpaceDialog = create<{
  open: boolean
  groupId: string | null
  setOpen: (v: boolean, groupId?: string | null) => void
}>((set) => ({
  open: false,
  groupId: null,
  setOpen: (open, groupId) => set(groupId !== undefined ? { open, groupId } : { open }),
}))

/** 全局「新任务」（`c`，04 §6）：页面登记当前上下文的默认值（空间 / 状态 / 截止）。 */
export interface NewTaskDefaults {
  spaceId?: string
  status?: 'inbox' | 'todo' | 'doing' | 'blocked' | 'done' | 'cancelled'
  dueAt?: string | null
  parentId?: string
}
export const useNewTask = create<{
  open: boolean
  defaults: NewTaskDefaults
  setOpen: (v: boolean) => void
  setDefaults: (d: NewTaskDefaults) => void
}>((set) => ({
  open: false,
  defaults: {},
  setOpen: (open) => set({ open }),
  setDefaults: (defaults) => set({ defaults }),
}))

/** 全局新记录（08 §2.8：`e`）的默认值：新记录建在哪（空间 / 目录位置）、什么类型、关联到谁。 */
export interface NewEntryDefaults {
  spaceId?: string
  kind?: EntryKind
  /** 预选模板（ADR-0011 §2）：内置 `builtin:<key>` 或用户模板 uuid */
  templateId?: string
  /** 目录（ADR-0012）：null = 放进目录根级；uuid = 作为其子页；undefined = 不进目录 */
  parentId?: string | null
  /** 自定义类型（ADR-0016；kind = 'custom' 时） */
  typeId?: string
  /** 新建并关联（ADR-0018）：新记录建好后由 `entryId` 指向它，关联类型 `kind` */
  linkFrom?: {
    entryId: string
    title: string
    kind: 'relates' | 'blocks' | 'caused_by' | 'resolves'
  }
}

/**
 * 打开时的有效默认值（ADR-0018、REQ-ENTRY-022）：一次性默认值（按钮显式传入）优先，
 * 否则取最后登记的页面上下文，都没有则为空（服务端落个人空间）。纯函数，便于单测。
 */
export function pickNewEntryDefaults(
  override: NewEntryDefaults | undefined,
  contexts: readonly { token: number; d: NewEntryDefaults }[],
): NewEntryDefaults {
  return override ?? contexts[contexts.length - 1]?.d ?? {}
}

let contextToken = 0
/**
 * 新建记录对话框（ADR-0018）：
 * - 页面用 `register(d)` 登记上下文（空间页 → 该空间目录根；记录页 → 同级），返回撤销函数，只撤自己那份
 *   （修 ADR-0014 起「一次性默认值被永久记住、e 沿用旧位置」的问题）；
 * - `setOpen(true, override?)`：打开瞬间把有效默认值冻结进 `defaults`，打开期间上下文变化不影响表单。
 */
export const useNewEntry = create<{
  open: boolean
  /** 本次打开冻结的有效默认值 */
  defaults: NewEntryDefaults
  contexts: { token: number; d: NewEntryDefaults }[]
  setOpen: (v: boolean, override?: NewEntryDefaults) => void
  register: (d: NewEntryDefaults) => () => void
}>((set, get) => ({
  open: false,
  defaults: {},
  contexts: [],
  setOpen: (open, override) =>
    set(open ? { open, defaults: pickNewEntryDefaults(override, get().contexts) } : { open }),
  register: (d) => {
    const token = ++contextToken
    set((s) => ({ contexts: [...s.contexts, { token, d }] }))
    return () => set((s) => ({ contexts: s.contexts.filter((c) => c.token !== token) }))
  },
}))

/** 当前编辑器的大纲（Aside 大纲页读取；EntryEditor 在每次更新后写入，REQ-EDITOR-012）。 */
/** 标题项（含章节编号 num 与层级 depth，与正文装饰 / 目录块同一算法，ADR-0024） */
export type OutlineItem = HeadingItem
export const useOutline = create<{
  items: OutlineItem[]
  jump: ((pos: number) => void) | null
  set: (items: OutlineItem[], jump?: ((pos: number) => void) | null) => void
}>((set) => ({
  items: [],
  jump: null,
  set: (items, jump) => set(jump === undefined ? { items } : { items, jump }),
}))

/**
 * ⌘K 上下文（04 §6 CommandContext）：页面登记底层（空间 / 记录），列表焦点 / 详情覆盖在上层（任务）。
 * 生效上下文 = focus ?? base。
 */
export type CommandTarget =
  | { kind: 'task'; id: string }
  | { kind: 'entry'; id: string }
  | { kind: 'space'; id: string; slug: string }
export const useCommandContext = create<{
  base: CommandTarget | null
  focus: CommandTarget | null
  setBase: (t: CommandTarget | null) => void
  setFocus: (t: CommandTarget | null) => void
}>((set) => ({
  base: null,
  focus: null,
  setBase: (base) => set({ base }),
  setFocus: (focus) => set({ focus }),
}))

/** ⌘K 面板与快捷键帮助的开关。 */
export const usePalette = create<{
  open: boolean
  help: boolean
  setOpen: (v: boolean) => void
  setHelp: (v: boolean) => void
}>((set) => ({
  open: false,
  help: false,
  setOpen: (open) => set({ open }),
  setHelp: (help) => set({ help }),
}))

/** Peek 预览（04 §6、REQ-UI-007）：不改 URL；Enter 升级为详情由面板处理。 */
export type PeekTarget =
  | { kind: 'task'; id: string; spaceSlug: string }
  | { kind: 'entry'; id: string }
export const usePeek = create<{
  target: PeekTarget | null
  open: (t: PeekTarget) => void
  close: () => void
}>((set) => ({
  target: null,
  open: (target) => set({ target }),
  close: () => set({ target: null }),
}))

/**
 * 记录详情坞（ADR-0054 §B）：列表 / 卡片 / 看板里单击记录在右侧坞内打开（≥ lg），不改 URL；
 * 换页（pathname 变化）即关闭。窄屏仍跳整页。
 */
export const useEntryDock = create<{
  id: string | null
  open: (id: string) => void
  close: () => void
}>((set) => ({
  id: null,
  open: (id) => set({ id }),
  close: () => set({ id: null }),
}))

/** 记录内锚定评论草稿（T1-023）：浮动工具条创建（已套 comment 标记）→ Aside 评论页写首条；取消时编辑器移除标记。 */
export const useCommentDraft = create<{
  pending: { threadId: string; quote: string } | null
  removeMark: ((threadId: string) => void) | null
  setPending: (p: { threadId: string; quote: string } | null) => void
  setRemover: (fn: ((threadId: string) => void) | null) => void
}>((set) => ({
  pending: null,
  removeMark: null,
  setPending: (pending) => set({ pending }),
  setRemover: (removeMark) => set({ removeMark }),
}))
